/**
 * AppShell — top-level page chrome, redesigned to the light token system.
 *
 * Desktop (≥ md):
 *   Sticky top header: wordmark · nav links · PIN badge · user menu.
 *
 * Mobile (< md):
 *   Compact top bar (wordmark + user menu) + fixed bottom tab bar.
 *   No hamburger drawer — navigation lives in the bottom tab bar.
 *
 * Exports a `useReadOnly` hook that pages use to know if write actions
 * should be hidden (role === 'boss').
 */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Calendar,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Settings2,
  User,
  Users,
  Wallet,
  Wrench,
  Zap,
} from 'lucide-react';
import { api } from '../api';
import { useWhoami } from '../lib/auth';
import { Pill } from './primitives';

// ── Instance meta ────────────────────────────────────────────────────────────
interface InstanceMeta {
  tenantId: string | null;
  displayName: string | null;
  brandLogoUrl: string | null;
  brandColor: string | null;
  readOnly: boolean;
  readOnlyMessage: string | null;
  demoMode: boolean;
}

function useInstanceMeta() {
  return useQuery<InstanceMeta>({
    queryKey: ['instanceMeta'],
    queryFn: () => api.get<InstanceMeta>('/api/_meta'),
    staleTime: 30_000, // 30s — matches Cache-Control header
    retry: false,
  });
}

// ── ReadOnly context ─────────────────────────────────────────────────────────
// true when user is `boss` OR when the workspace is suspended.
const ReadOnlyCtx = createContext(false);
/** Returns true when the logged-in user is `boss` OR the workspace is suspended. */
export function useReadOnly() {
  return useContext(ReadOnlyCtx);
}

// ── Nav definitions ─────────────────────────────────────────────────────────
const NAV_FULL = [
  { to: '/',        label: 'Dashboard', icon: LayoutDashboard },
  { to: '/batches', label: 'Batches',   icon: Calendar        },
  { to: '/ledgers', label: 'Ledgers',   icon: BookOpen        },
  { to: '/accounts',label: 'Accounts',  icon: Wallet          },
  { to: '/match',   label: 'Match',     icon: Zap             },
  { to: '/settings',label: 'Settings',  icon: Settings2       },
] as const;

const TABS = [
  { to: '/batches', label: 'Batches',  icon: Calendar  },
  { to: '/ledgers', label: 'Ledgers',  icon: BookOpen  },
  { to: '/match',   label: 'Match',    icon: Zap       },
  { to: '/settings',label: 'Settings', icon: Settings2 },
] as const;

// ── Style helpers ───────────────────────────────────────────────────────────
const desktopLink = (active: boolean) =>
  [
    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-small font-medium transition-colors',
    active
      ? 'bg-brand-light text-brand'
      : 'text-ink-muted hover:bg-surface-raised hover:text-ink',
  ].join(' ');

const tabLink = (active: boolean) =>
  [
    'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
    active ? 'text-brand' : 'text-ink-muted',
  ].join(' ');

// ── AppShell ────────────────────────────────────────────────────────────────
export function AppShell({
  title,
  actions,
  toolbar,
  children,
}: {
  title: string;
  /** @deprecated — pass `toolbar={<PageToolbar …/>}` instead. */
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}) {
  const who = useWhoami();
  const meta = useInstanceMeta();
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const isOwner = who.data?.state === 'authenticated' && who.data.role === 'owner';
  const isBoss  = who.data?.state === 'authenticated' && who.data.role === 'boss';
  const username = who.data?.state === 'authenticated' ? who.data.username : '';
  // Impersonation: the whoami response includes impersonatedBy when the session
  // was created via /api/_admin/impersonate.
  const impersonatedBy =
    who.data?.state === 'authenticated'
      ? (who.data as { impersonatedBy?: string | null }).impersonatedBy ?? null
      : null;

  const workspaceSuspended = meta.data?.readOnly ?? false;
  const suspendMessage = meta.data?.readOnlyMessage ?? null;
  const demoMode = meta.data?.demoMode ?? false;

  // document.title from displayName or tenantId
  const displayName = meta.data?.displayName ?? meta.data?.tenantId ?? null;
  useEffect(() => {
    if (displayName) document.title = displayName;
  }, [displayName]);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Close user menu on outside click / Escape
  useEffect(() => {
    if (!userMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const logoutMut = useMutation({
    mutationFn: () => api.post<unknown>('/api/auth/logout'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/login', { replace: true });
    },
  });

  // Dev-mode warning for deprecated prop
  useEffect(() => {
    if (actions) {
      // eslint-disable-next-line no-console
      console.warn('[AppShell] `actions` prop is deprecated. Use `toolbar` slot.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReadOnlyCtx.Provider value={isBoss || workspaceSuspended}>
      <div className="min-h-screen bg-bg font-ui text-ink">

        {/* ── Demo mode banner (always on top in demo) ──────────────────── */}
        {demoMode && (
          <div className="sticky top-0 z-50 bg-yellow-400 px-4 py-2 text-center text-small font-semibold text-yellow-950">
            Demo mode — your changes reset every day at midnight UTC.
          </div>
        )}

        {/* ── Workspace suspended banner ───────────────────────────────── */}
        {workspaceSuspended && (
          <div className="sticky top-0 z-40 bg-red-600 px-4 py-2 text-center text-small font-medium text-white">
            {suspendMessage ?? 'This workspace is currently suspended.'}
          </div>
        )}

        {/* ── Impersonation banner ──────────────────────────────────────── */}
        {impersonatedBy && !workspaceSuspended && (
          <div className="sticky top-0 z-40 flex items-center justify-between bg-amber-400 px-4 py-1.5 text-small font-medium text-amber-900">
            <span>Signed in as owner — operator: {impersonatedBy}</span>
            <button
              type="button"
              onClick={() => {
                void api.post('/api/auth/logout').then(() => {
                  void qc.invalidateQueries({ queryKey: ['whoami'] });
                  window.location.href = '/';
                });
              }}
              className="rounded px-2 py-0.5 text-caption font-semibold underline hover:no-underline"
            >
              Exit impersonation
            </button>
          </div>
        )}

        {/* ── Top header ─────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-30 border-b border-border bg-surface shadow-sm">
          <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-4 sm:px-6">

            {/* Left: wordmark + desktop nav */}
            <div className="flex min-w-0 items-center gap-3">
              <span className="shrink-0 text-body font-semibold tracking-tight text-ink">
                {displayName ?? 'SRB'} <span className="text-brand">Accounts</span>
              </span>
              {/* Desktop nav */}
              <nav className="ml-1 hidden items-center gap-0.5 md:flex">
                {NAV_FULL.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) => desktopLink(isActive)}
                  >
                    <Icon className="h-[16px] w-[16px]" strokeWidth={1.75} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </nav>
            </div>

            {/* Right: PIN badge + legacy actions + user menu */}
            <div className="flex shrink-0 items-center gap-2">
              {who.data?.state === 'authenticated' && (
                <PinUnlockedBadge unlockedUntil={who.data.pinUnlockedUntil} />
              )}

              {isBoss && (
                <span className="hidden rounded-md bg-amber-50 px-2 py-0.5 text-caption font-medium text-amber-700 ring-1 ring-amber-200 sm:inline">
                  read-only
                </span>
              )}

              {actions && (
                <div className="hidden items-center gap-2 md:flex">{actions}</div>
              )}

              {who.data?.state === 'authenticated' && (
                <div ref={userMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-small text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
                  >
                    <User className="h-4 w-4" strokeWidth={1.75} />
                    <span className="hidden max-w-[100px] truncate sm:inline">{username}</span>
                    <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                  </button>

                  {userMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full z-40 mt-1 min-w-[200px] overflow-hidden rounded-card border border-border bg-surface py-1 shadow-[0_4px_16px_rgba(0,0,0,0.1)]"
                    >
                      <UserMenuItem to="/account" icon={User} label="My account" />
                      <UserMenuItem to="/wizard" icon={Wrench} label="Setup wizard" />
                      {isOwner && (
                        <UserMenuItem to="/settings/users" icon={Users} label="Users" />
                      )}
                      <div className="my-1 h-px bg-border" />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setUserMenuOpen(false);
                          logoutMut.mutate();
                        }}
                        className="flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small text-neg transition-colors hover:bg-red-50"
                      >
                        <LogOut className="h-4 w-4" strokeWidth={1.75} />
                        <span>Sign out</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Page toolbar (optional, second sticky row) ───────────────── */}
        {toolbar}

        {/* ── Page content ─────────────────────────────────────────────── */}
        <main className="mx-auto max-w-7xl px-4 py-5 pb-24 sm:px-6 sm:py-6 md:pb-6">
          {!toolbar && (
            <h2 className="mb-4 text-caption font-semibold uppercase tracking-widest text-ink-muted">
              {title}
            </h2>
          )}
          {children}
        </main>

        {/* ── Mobile bottom tab bar ─────────────────────────────────────── */}
        <nav
          aria-label="Main navigation"
          className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface shadow-[0_-1px_0_0_var(--border)] md:hidden"
        >
          {TABS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={false}
              className={({ isActive }) => tabLink(isActive)}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>

      </div>
    </ReadOnlyCtx.Provider>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function UserMenuItem({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className="flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small text-ink transition-colors hover:bg-surface-raised"
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  );
}

function PinUnlockedBadge({ unlockedUntil }: { unlockedUntil: string | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!unlockedUntil) return;
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, [unlockedUntil]);
  if (!unlockedUntil) return null;
  const remainingMs = new Date(unlockedUntil).getTime() - now;
  if (remainingMs <= 0) return null;
  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);
  return (
    <Pill tone="green">
      PIN {minutes}:{seconds.toString().padStart(2, '0')}
    </Pill>
  );
}
